import { useCallback, useEffect, useState } from 'react';

import '@xyflow/react/dist/style.css';
import { HStack, IconButton, ModalHeader, Tab, TabList, Tabs, useColorModeValue } from '@ecoinvent/ui';
import { ReactFlowProvider } from '@xyflow/react';
import { FiX } from 'react-icons/fi';
import { localStorageKeys } from 'storageKeys';

import GraphFullscreenModal from 'components/shared/GraphFullscreenModal';
import { SupplyChainNode } from 'hooks/data/datasets/V4/supplyChain/types';
import { DerivedEdge, NodeCounts, ShownCounts } from 'hooks/data/datasets/V4/supplyChain/useSupplyChain';
import useCloseableAlertState from 'hooks/useCloseableAlertState';

import SupplyChainCanvas from './SupplyChainCanvas';
import { SUPPLY_CHAIN_INFO_STEPS } from './supplyChainInfoSteps';
import { LciaSelection } from './SupplyChainLciaFilters';

type SupplyChainMode = 'flows' | 'impact_assessment';

type HeaderProps = {
  mode: SupplyChainMode;
  onModeChange: (m: SupplyChainMode) => void;
  onClose: () => void;
};

const SupplyChainFullscreenHeader = ({ mode, onModeChange, onClose }: HeaderProps) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  return (
    <ModalHeader
      px={4}
      py={2}
      borderBottom="1px"
      borderColor={borderColor}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <Tabs
        overflowY="hidden"
        alignSelf="flex-start"
        size="sm"
        index={mode === 'flows' ? 0 : 1}
        onChange={(i) => onModeChange(i === 0 ? 'flows' : 'impact_assessment')}
      >
        <TabList maxW="100%" w="max-content">
          <Tab fontWeight={mode === 'flows' ? 'semibold' : 'normal'}>Flows</Tab>
          <Tab fontWeight={mode === 'impact_assessment' ? 'semibold' : 'normal'}>Impact assessment</Tab>
        </TabList>
      </Tabs>
      <HStack spacing={2}>
        <IconButton
          aria-label="Close fullscreen"
          icon={<FiX size={16} />}
          size="sm"
          variant="ghost"
          colorScheme="gray"
          onClick={onClose}
        />
      </HStack>
    </ModalHeader>
  );
};

type Props = {
  nodes: SupplyChainNode[];
  edges: DerivedEdge[];
  nodeCounts: NodeCounts;
  shownCounts: ShownCounts;
  rootNodeId: string;
  expandedNodeIds: Set<string>;
  expandedOutputNodeIds: Set<string>;
  mode: 'flows' | 'impact_assessment';
  selectedMethodId: string;
  lciaSelection: LciaSelection | null;
  onLciaChange: (selection: LciaSelection) => void;
  onExpand: (id: string) => void;
  onCollapse: (id: string, activeNodeId?: string | null) => void;
  onExpandOutputs: (id: string) => void;
  onCollapseOutputs: (id: string, activeNodeId?: string | null) => void;
  onCollapseAll: () => void;
  onViewDataset: (id: string) => void;
  showMoreInputs: (id: string) => void;
  showMoreOutputs: (id: string) => void;
};

const SupplyChainGraph = ({
  nodes,
  edges,
  nodeCounts,
  shownCounts,
  rootNodeId,
  expandedNodeIds,
  expandedOutputNodeIds,
  mode,
  selectedMethodId,
  lciaSelection,
  onLciaChange,
  onExpand,
  onCollapse,
  onExpandOutputs,
  onCollapseOutputs,
  onCollapseAll,
  onViewDataset,
  showMoreInputs,
  showMoreOutputs,
}: Props) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullscreenMode, setFullscreenMode] = useState<SupplyChainMode>(mode);
  // Owned here, not in the canvas: each fullscreen toggle mounts a new canvas, resetting the step.
  const { isOpen: isInfoCardOpen, onClose: onInfoCardClose } = useCloseableAlertState(
    localStorageKeys.SUPPLY_CHAIN_INFO_CARD_DISMISSED
  );
  const [infoStepIndex, setInfoStepIndex] = useState(0);
  const onInfoCardNext = useCallback(() => setInfoStepIndex((i) => Math.min(i + 1, SUPPLY_CHAIN_INFO_STEPS.length - 1)), []);

  useEffect(() => {
    if (isFullScreen) setFullscreenMode(mode);
  }, [isFullScreen, mode]);

  const onToggleFullScreen = useCallback(() => setIsFullScreen((v) => !v), []);

  const canvasProps = {
    nodes,
    edges,
    nodeCounts,
    shownCounts,
    rootNodeId,
    expandedNodeIds,
    expandedOutputNodeIds,
    selectedMethodId,
    lciaSelection,
    onLciaChange,
    onExpand,
    onCollapse,
    onExpandOutputs,
    onCollapseOutputs,
    onCollapseAll,
    onViewDataset,
    showMoreInputs,
    showMoreOutputs,
    onToggleFullScreen,
    isInfoCardOpen,
    infoStepIndex,
    onInfoCardNext,
    onInfoCardClose,
  };

  return (
    <>
      {!isFullScreen && (
        <ReactFlowProvider>
          <SupplyChainCanvas {...canvasProps} mode={mode} isFullScreen={false} />
        </ReactFlowProvider>
      )}

      <GraphFullscreenModal
        isOpen={isFullScreen}
        onClose={() => setIsFullScreen(false)}
        header={
          <SupplyChainFullscreenHeader
            mode={fullscreenMode}
            onModeChange={setFullscreenMode}
            onClose={() => setIsFullScreen(false)}
          />
        }
      >
        {isFullScreen && <SupplyChainCanvas {...canvasProps} mode={fullscreenMode} isFullScreen={true} />}
      </GraphFullscreenModal>
    </>
  );
};

export default SupplyChainGraph;
